import { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Paper,
  Typography,
  Stack,
} from "@mui/material";
import { isEmpty } from "lodash";
import { useFetchEmployeeBalances } from "src/Pages/Company/Absences/api/useEmployee";
import __ from "src/utils/Translations";
import { LogLevel, useLogStatus } from "src/utils/newRelic/useLogStatus";
import transformBalances from "src/utils/Company/Absences/transformBalances";
import ErrorDataFetching from "src/Components/Error/ErrorDataFetching";
import { Loader } from "src/Components/Loader/Loader";
import AbsenceTypeList from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecordsBalances/AbsenceTypeList";

const AbsenceRecordsBalances: FC = () => {
  const { employeeId } = useParams<{ employeeId: string }>();
  const logStatus = useLogStatus();
  const [activeTab, setActiveTab] = useState<"active" | "inactive">("active");

  if (!employeeId) {
    logStatus({
      level: LogLevel.DEBUG,
      message: "No employee ID provided.",
      file: "AbsenceRecordsBalances.tsx",
    });
  }

  const {
    data: balancesData,
    isFetching: isFetchingBalances,
    isError: isErrorBalances,
  } = useFetchEmployeeBalances(employeeId);

  const { active: activeBalances, inactive: inactiveBalances } = useMemo(() => {
    if (!balancesData)
      return {
        active: { absenceTypes: [], attendanceTypes: [] },
        inactive: { absenceTypes: [], attendanceTypes: [] },
      };

    return transformBalances(balancesData);
  }, [balancesData]);

  const handleTabChange = (
    _event: React.MouseEvent<HTMLElement>,
    newTab: "active" | "inactive"
  ) => {
    if (newTab !== null) {
      setActiveTab(newTab);
    }
  };

  const hasActiveContent =
    !isEmpty(activeBalances.absenceTypes) ||
    !isEmpty(activeBalances.attendanceTypes);
  const hasInactiveContent =
    !isEmpty(inactiveBalances.absenceTypes) ||
    !isEmpty(inactiveBalances.attendanceTypes);

  return (
    <>
      {isErrorBalances ? (
        <ErrorDataFetching />
      ) : (
        <Box sx={{ position: "relative" }} data-testid="">
          {isFetchingBalances ? (
            <Loader />
          ) : (
            <>
              <Stack direction="row" justifyContent="space-between" mb="1rem">
                <Typography variant="h6" component="h2">
                  {__("Wymiary nieobecności i obecności")}
                </Typography>
                <ToggleButtonGroup
                  value={activeTab}
                  exclusive
                  onChange={handleTabChange}
                  aria-label={__("Wybierz typ wymiaru")}
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                    },
                  }}
                >
                  <ToggleButton
                    value="active"
                    aria-label={__("Wymiary aktywne")}
                  >
                    {__("Aktywne")}
                  </ToggleButton>
                  <ToggleButton
                    value="inactive"
                    aria-label={__("Wymiary nieaktywne")}
                  >
                    {__("Nieaktywne")}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Box>
                {/* Active Content */}
                <Box
                  sx={{ display: activeTab === "active" ? "block" : "none" }}
                >
                  {hasActiveContent ? (
                    <>
                      <AbsenceTypeList
                        employeeId={employeeId}
                        label="Nieobecności"
                        data={activeBalances.absenceTypes}
                      />
                      <AbsenceTypeList
                        employeeId={employeeId}
                        label="Statusy obecności"
                        data={activeBalances.attendanceTypes}
                      />
                    </>
                  ) : (
                    <Alert severity="info">
                      {__("Brak wniosków do wyświetlenia.")}
                    </Alert>
                  )}
                </Box>

                {/* Inactive Content */}
                <Box
                  sx={{ display: activeTab === "inactive" ? "block" : "none" }}
                >
                  {hasInactiveContent ? (
                    <>
                      <AbsenceTypeList
                        employeeId={employeeId}
                        label="Nieobecności"
                        data={inactiveBalances.absenceTypes}
                      />
                      <AbsenceTypeList
                        employeeId={employeeId}
                        label="Statusy obecności"
                        data={inactiveBalances.attendanceTypes}
                      />
                    </>
                  ) : (
                    <Alert severity="info">
                      {__("Brak wniosków do wyświetlenia.")}
                    </Alert>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default AbsenceRecordsBalances;
